import React, { useState, useContext, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { breakpoint, sizes } from 'variables';
import type { TopBannerListModel } from 'autogen/swagger/all';
import TopBannerItem from 'components/topBanners/topBannerItem';
import { emblaHOC, useNavigation } from 'components/emblaCarousel/emblaCarousel';
import { Container, Viewport, EmblaContext } from 'components/emblaCarousel';
import { SvgSprite } from 'modules/svgSprite';
import type { EmblaOptionsType } from 'embla-carousel';
import { ColorNeutralWhite, Spacing16, Spacing4 } from 'autogen/design-tokens/tokens';
import { useTranslation } from 'react-i18next';
import { SlideToTheNext, SlideToThePrev } from 'autogen/translation-keys/trans-website-shared';

enum NavigationDirection {
    prev = 'prev',
    next = 'next'
}

const TopBannersList = styled.div`
    position: relative;
    display: flex;
    background: ${ColorNeutralWhite};
`;

const StyledViewport = styled(Viewport)`
    flex: 0 0 100%;
`;

const StyledContainer = styled(Container)`
    display: flex;
    flex: 0 1 100%;
`;

const ArrowButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 100%;
    background-color: ${ColorNeutralWhite};
`;

const ArrowClickableContainer = styled.button<{ enabled: boolean; direction: NavigationDirection }>`
    display: none;
    border-radius: 0;

    ${breakpoint.up(sizes.sm)} {
        position: absolute;
        top: 50%;
        height: 100%;
        width: 80px;
        transform: translateY(-50%);
        display: flex;
        align-items: center;
        background: linear-gradient(
            ${({ direction }): number => (direction === NavigationDirection.prev ? 90 : -90)}deg,
            ${ColorNeutralWhite} 18.23%,
            rgb(from ${ColorNeutralWhite} r g b / 0%) 100%
        );
        z-index: 1;

        ${({ direction }): string => (direction === NavigationDirection.prev ? 'left: 0px' : 'right: 0px')};
        ${({ enabled }) => !enabled && 'visibility: hidden;'}
    }
`;

const ArrowElement = styled(SvgSprite)<{ direction?: NavigationDirection }>`
    transform: rotate(${({ direction }): number => (direction === NavigationDirection.prev ? 90 : -90)}deg);
    height: 12px;
    width: 12px;
`;

const StyledTopBannerItem = styled(TopBannerItem)`
    &:first-child {
        padding: 0 ${Spacing4} 0 ${Spacing16};
    }

    &:last-child {
        padding: 0 ${Spacing16} 0 ${Spacing4};
    }
`;

function Arrow(props: { direction: NavigationDirection }): JSX.Element {
    const { direction } = props;
    const { t } = useTranslation();

    const [, embla] = useContext(EmblaContext);
    const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
    const [nextBtnEnabled, setNextBtnEnabled] = useState(false);

    const { next, prev, canScrollNext, canScrollPrev } = useNavigation();

    const onSelect = useCallback(() => {
        setPrevBtnEnabled(canScrollPrev);
        setNextBtnEnabled(canScrollNext);
    }, [canScrollPrev, canScrollNext]);

    useEffect(() => {
        if (!embla) return;
        onSelect();
        embla.on('select', onSelect);
    }, [embla, onSelect]);

    const handleClick = (): void => {
        if (direction === NavigationDirection.next) {
            next();
        } else {
            prev();
        }
    };

    return (
        <ArrowClickableContainer
            type="button"
            className="btn"
            aria-label={direction === NavigationDirection.next ? t(SlideToTheNext) : t(SlideToThePrev)}
            onClick={handleClick}
            direction={direction}
            enabled={direction === NavigationDirection.next ? nextBtnEnabled : prevBtnEnabled}
        >
            <ArrowButton>
                <ArrowElement sprite="Arrow" direction={direction} />
            </ArrowButton>
        </ArrowClickableContainer>
    );
}

function CarouselList({ topBanners }: TopBannerListModel): JSX.Element {
    return (
        <TopBannersList>
            {topBanners.length > 2 && <Arrow direction={NavigationDirection.prev} />}
            <StyledViewport>
                <StyledContainer>
                    {topBanners.map((topBanner, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <StyledTopBannerItem {...topBanner} key={index} />
                    ))}
                </StyledContainer>
            </StyledViewport>
            {topBanners.length > 2 && <Arrow direction={NavigationDirection.next} />}
        </TopBannersList>
    );
}

const emblaOptions: EmblaOptionsType = {
    align: 'start',
    containScroll: 'trimSnaps',
    slidesToScroll: 1
};

export default emblaHOC(emblaOptions)(CarouselList);
